import { isPlaceholderStudentName, normalizeStudentNameInput } from './studentNamePolicy';

const HEADER_WORDS = new Set(['번호', '이름', '성명', '학생명', '학생', '학생 이름', 'no', 'name']);
const NUMBER_CELL = /^\d{1,3}\s*[.)번]?$/;
const LEADING_NUMBER = /^\d{1,3}\s*[.)번]?\s+/;

// 서버 ROSTER_MAX_STUDENTS와 같다.
export const ROSTER_MAX_STUDENTS = 80;

export const SOURCE_LABELS = {
  batch: '생기부에서 저장',
  scoring: '채점에서 저장',
  paste: '직접 만듦',
  file: '파일로 만듦',
};

export const semesterLabel = (semester) => `${semester === 2 ? 2 : 1}학기`;

const pickNameCell = (line) => {
  const cells = line.split(/\t|,/).map(cell => normalizeStudentNameInput(cell)).filter(Boolean);
  const cell = cells.find(value => !NUMBER_CELL.test(value));
  return cell ? cell.replace(LEADING_NUMBER, '') : '';
};

/** 엑셀·한글 표에서 복사한 명렬표(번호 열, 머리글 포함)를 이름 목록으로 바꾼다. */
export const parsePastedNames = (text) => String(text || '')
  .split(/\r?\n/)
  .map(pickNameCell)
  .filter(name => name && !HEADER_WORDS.has(name.toLowerCase()) && !isPlaceholderStudentName(name));

/** 학급 관리 화면: 학년도(최근 먼저) → 학기(2학기 먼저) 묶음. */
export const groupRostersByTerm = (rosters, currentSchoolYear) => {
  const years = new Map();
  (rosters || []).forEach((roster) => {
    const year = Number(roster.school_year) || currentSchoolYear;
    if (!years.has(year)) years.set(year, new Map());
    const terms = years.get(year);
    const semester = roster.semester === 2 ? 2 : 1;
    if (!terms.has(semester)) terms.set(semester, []);
    terms.get(semester).push(roster);
  });
  return [...years.entries()]
    .sort(([a], [b]) => b - a)
    .map(([year, terms]) => ({
      year,
      when: year > currentSchoolYear ? 'next' : year < currentSchoolYear ? 'past' : 'current',
      terms: [...terms.entries()].sort(([a], [b]) => b - a).map(([semester, items]) => ({ semester, rosters: items })),
    }));
};

/** 새 학년 반 배정이 나오는 12~2월에만 "다음 학년도 명단"을 연다. */
export const canPrepareNextYear = (date = new Date()) => [11, 0, 1].includes(date.getMonth());

export const toEditableStudents = (roster) => (roster?.students || []).map(student => ({
  sid: student.sid,
  name: student.name || '',
  aliases: [...(student.aliases || [])],
}));

const normalizedRows = (students) => (students || [])
  .map(s => ({ sid: s.sid || '', name: normalizeStudentNameInput(s.name), aliases: [...(s.aliases || [])] }))
  .filter(s => s.name);

export const studentsChanged = (roster, students) => (
  JSON.stringify(normalizedRows(toEditableStudents(roster))) !== JSON.stringify(normalizedRows(students))
);

/** 선생님이 뺀 별칭만 removed_aliases 로 보낸다. 화면을 연 뒤 서버가 새로 배운 별칭은 서버가 지킨다. */
export const toStudentPatch = (students, roster) => {
  const original = new Map((roster?.students || []).map(s => [s.sid, s.aliases || []]));
  return normalizedRows(students).map((s) => {
    const removed = s.sid ? (original.get(s.sid) || []).filter(alias => !s.aliases.includes(alias)) : [];
    return {
      ...(s.sid ? { sid: s.sid } : {}),
      name: s.name,
      aliases: s.aliases,
      ...(removed.length ? { removed_aliases: removed } : {}),
    };
  });
};

/** 명단 수정 로그용 요약. 이름은 빼고 몇 명을 더하고·빼고·고치고·옮겼는지만 센다. */
export const summarizeRosterEdit = (roster, patch = {}) => {
  const before = roster?.students || [];
  const after = patch.students || before;
  const beforeById = new Map(before.map(s => [s.sid, s]));
  const keptIds = after.filter(s => s.sid && beforeById.has(s.sid)).map(s => s.sid);
  const keptSet = new Set(keptIds);
  const beforeOrder = before.filter(s => keptSet.has(s.sid)).map(s => s.sid);
  return {
    added: after.filter(s => !s.sid || !beforeById.has(s.sid)).length,
    removed: before.filter(s => !keptSet.has(s.sid)).length,
    renamed: keptIds.filter(sid => normalizeStudentNameInput(beforeById.get(sid).name) !== normalizeStudentNameInput(after.find(s => s.sid === sid).name)).length,
    aliases_removed: after.reduce((sum, s) => sum + (s.removed_aliases || []).length, 0),
    reordered: keptIds.some((sid, index) => beforeOrder[index] !== sid),
    label_changed: patch.label !== undefined && String(patch.label).trim() !== String(roster?.label || '').trim(),
    semester_changed: patch.semester !== undefined && Number(patch.semester) !== Number(roster?.semester || 1),
  };
};

/** 생기부 다중 생성·채점에 바로 쓰는 명렬표 엑셀 행. */
export const buildNameSheetRows = (roster) => [
  ['번호', '학생명', '기록'],
  ...(roster?.students || []).map((student, index) => [student.number || index + 1, student.name, '']),
];

export const nameSheetFileName = (roster) => {
  const label = String(roster?.label || '우리 반').replace(/[\\/:*?"<>|]/g, ' ').trim() || '우리 반';
  return `${label}_명렬표.xlsx`;
};
